import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Check, Shield, Dumbbell, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  spaCollage: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/ta2szmkb_file_0000000000947230a410da9a77c2c8e9.png",
  gardenCollage: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/me9gpghq_file_000000001ef471f8805d3e317caa5f32.png",
  gardenView: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/2bs82ywh_file_000000009670723086d11c03fb0af355.png",
  drum: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/vmj32u17_IMG-20260826-WA0053.webp",
  singingBowls: "https://customer-assets-rejwkqb3.emergentagent.net/job_9d2782f9-5276-4e92-beba-9f63a63d9a7f/artifacts/0yge4pwh_IMG-20260826-WA0054.webp",
};

const treatments = [
  {
    name: "Restoration Massage",
    duration: "60 Minutes",
    price: 100,
    deposit: 20,
    description: "A professional full-body massage designed to help release muscular tension, ease built-up stress and leave you feeling deeply relaxed and restored.",
    detail: "The treatment is tailored to the individual's body and areas of tension.",
  },
  {
    name: "Restoration Massage",
    duration: "90 Minutes",
    price: 150,
    deposit: 20,
    description: "A longer, more immersive treatment allowing additional time to work through areas of tension while creating a deeply relaxing and restorative experience.",
    detail: "The additional time allows the treatment to be more thorough and completely unhurried.",
  },
  {
    name: "Restoration Massage",
    duration: "2 Hours",
    price: 200,
    deposit: 20,
    description: "The ultimate restorative experience. A full two hours dedicated to deep relaxation, total tension release and complete restoration of mind and body.",
    detail: "The extended session allows for a fully immersive, unhurried treatment covering the entire body.",
  },
];

const benefits = [
  { icon: Shield, title: "Tension Release", text: "Targeted work on areas of muscular tightness and built-up stress" },
  { icon: Dumbbell, title: "Recovery", text: "Support your body's natural recovery from training, work, and daily demands" },
  { icon: Heart, title: "Relaxation", text: "Switch off from the noise and drop into a state of deep rest and calm" },
];

export default function MensTreatmentsPage() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services?category=mens`);
        setServices(res.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const handleBook = (treatment) => {
    const durationMap = { "60 Minutes": 60, "90 Minutes": 90, "2 Hours": 120 };
    const durationMins = durationMap[treatment.duration];
    const matchedService = services.find(s => 
      s.duration_minutes === durationMins && s.category === "mens"
    );
    if (matchedService) {
      navigate(`/booking?service=${matchedService.id}`);
    } else {
      navigate("/booking?category=mens");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="mens-treatments-page">
      {/* Hero with Garden Studio Image */}
      <section className="relative pt-32 pb-24 bg-[#1a1a1a] text-white overflow-hidden min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={IMAGES.gardenCollage}
            alt="Sacred Sounds Studio garden and treatment space"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/70 via-[#1a1a1a]/50 to-[#1a1a1a]" />
        </div>
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-6">
              Specialist Massage
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light mb-6 text-white" data-testid="mens-title">
              Men's Massage <em className="italic text-[#C9A96E]">Treatments</em>
            </h1>
            <p className="text-lg text-gray-300 max-w-xl mx-auto leading-relaxed">
              Professional, restorative massage designed specifically for men. 
              A space to switch off, release tension, and restore your body.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction with Spa Collage */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
                Designed For You
              </p>
              <h2 className="heading-lg mb-8">
                Your Time to <em className="italic">Restore</em>
              </h2>
              <p className="body-text text-muted-foreground leading-relaxed mb-6">
                These treatments are designed specifically for men looking for a professional, 
                relaxing and restorative massage experience. Whether you're dealing with the 
                physical demands of training, long hours at a desk, or simply need time to 
                switch off — this is your space.
              </p>
              <p className="body-text text-muted-foreground leading-relaxed">
                Each session is focused on relaxation, muscular tension release, recovery, 
                stress relief, and overall wellbeing. Tailored entirely to you.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES.spaCollage}
                  alt="Sacred Sounds Studio massage treatment room and amenities"
                  className="w-full h-full object-cover"
                  data-testid="mens-studio-image"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-[#C9A96E]/20" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-8"
              >
                <benefit.icon className="w-8 h-8 text-[#C9A96E] mx-auto mb-4" />
                <h3 className="font-serif text-lg text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="section-spacing" data-testid="mens-treatment-list">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
              Treatments & Pricing
            </p>
            <h2 className="heading-lg">
              Restoration <em className="italic">Massage</em>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-8">
            {treatments.map((treatment, index) => (
              <motion.div
                key={`${treatment.name}-${treatment.duration}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="border border-border bg-background p-8 lg:p-10"
                data-testid={`mens-treatment-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="w-4 h-4 text-[#C9A96E]" />
                      <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                        {treatment.duration}
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl text-foreground mb-3">
                      {treatment.name}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                      {treatment.description}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed italic">
                      {treatment.detail}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-end gap-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="font-serif text-3xl text-foreground">
                        £{treatment.price}
                      </p>
                      <p className="text-xs text-[#C9A96E] mt-1">
                        £{treatment.deposit} deposit to book
                      </p>
                    </div>
                    <Button
                      onClick={() => handleBook(treatment)}
                      className="rounded-none bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white px-8 py-6 text-xs tracking-[0.2em] uppercase transition-all"
                      data-testid={`mens-book-${index}`}
                    >
                      Book Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Deposit Breakdown */}
                <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-6 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[#C9A96E]" />
                    £{treatment.deposit} deposit secures your booking
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[#C9A96E]" />
                    £{treatment.price - treatment.deposit} remaining on arrival
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-[#C9A96E]" />
                    Cash or bank transfer
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Garden Image Break */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src={IMAGES.gardenView}
          alt="Sacred Sounds Studio garden and relaxation space"
          className="w-full h-full object-cover"
          data-testid="mens-garden-image"
        />
        <div className="absolute inset-0 bg-[#1a1a1a]/20" />
      </section>

      {/* What to Expect */}
      <section className="section-spacing bg-[#1a1a1a]" data-testid="mens-what-to-expect">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-12">
                <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
                  Your Experience
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl font-light text-white">
                  What to <em className="italic text-[#C9A96E]">Expect</em>
                </h2>
              </div>

              <div className="space-y-8">
                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">1</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Private Studio</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Your treatment takes place in my private studio in a calm, comfortable and professional environment.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">2</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Consultation</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      When you arrive, we will briefly discuss any areas of tension, your preferences and what you would like to get from the treatment.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">3</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Preparation</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      You will then be given privacy to get comfortable on the massage table before the treatment begins.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="w-8 h-8 rounded-full bg-[#C9A96E]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-xs font-medium text-[#C9A96E]">4</span>
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-white mb-2">Your Treatment</h4>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      The massage is tailored to your body and focuses on relaxation, muscular tension and restoration. Everything is professional, respectful and centred around your wellbeing.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Singing bowls image alongside What to Expect */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="aspect-[3/4] overflow-hidden sticky top-32">
                <img
                  src={IMAGES.singingBowls}
                  alt="Crystal singing bowls at Sacred Sounds Studio"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Professional & Respectful Experience */}
      <section className="relative section-spacing bg-background overflow-hidden" data-testid="mens-professional-section">
        <div className="absolute inset-0 opacity-[0.04]">
          <img
            src={IMAGES.drum}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-10">
              <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
                Please Read
              </p>
              <h2 className="heading-lg">
                A Professional & Respectful <em className="italic">Experience</em>
              </h2>
            </div>

            <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
              <p>
                My Men's Massage Treatments are designed to be a deeply relaxing, sensory and restorative experience. I create a calm, comfortable environment where you can switch off, relax and allow your body to fully unwind.
              </p>
              <p>
                Although the treatment may feel soothing and sensual in nature, it is always completely professional and non-sexual. Sensuality is part of creating a relaxing massage experience and should never be interpreted as an invitation for sexual behaviour or interaction.
              </p>
              <p>
                Mutual respect and professional boundaries are really important to me. I ask that you come with the same intention — to relax, unwind and enjoy the treatment.
              </p>
              <p className="font-medium text-foreground">
                Any inappropriate comments, requests or behaviour will not be tolerated. If professional boundaries are crossed or I feel uncomfortable at any point, I reserve the right to end the treatment immediately, without a refund of the deposit or any payment made.
              </p>
              <p>
                Thank you for respecting the space, the treatment and the professional experience I provide.
              </p>
              <p className="font-serif text-lg text-[#C9A96E] italic pt-4">
                Relax. Unwind. Reset.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cancellation Policy */}
      <section className="section-spacing bg-muted">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#C9A96E] mb-4">
              Important Information
            </p>
            <h2 className="heading-lg mb-8">
              Booking <em className="italic">Policy</em>
            </h2>
            <div className="text-left space-y-4">
              <div className="bg-background border border-border p-6">
                <h4 className="font-serif text-lg mb-2">Deposit</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A £20 deposit is required at the time of booking to secure your appointment. 
                  The deposit is deducted from the total treatment price. 
                  The remaining balance is payable on arrival by cash or bank transfer.
                </p>
              </div>
              <div className="bg-background border border-border p-6">
                <h4 className="font-serif text-lg mb-2">Cancellations</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A minimum of 48 hours' notice is required to cancel. With 48+ hours' notice, 
                  your deposit can be refunded. With less than 48 hours' notice, 
                  the £20 deposit is non-refundable.
                </p>
              </div>
              <div className="bg-background border border-border p-6">
                <h4 className="font-serif text-lg mb-2">Rescheduling</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A minimum of 48 hours' notice is required to reschedule. With 48+ hours' notice, 
                  your deposit can be transferred to a new date. With less than 48 hours' notice, 
                  the £20 deposit is forfeited and cannot be transferred.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA with Garden Image */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.gardenView}
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-[#1a1a1a]/90" />
        </div>
        <div className="container-custom text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-white mb-6">
              Ready to <em className="italic text-[#C9A96E]">Restore?</em>
            </h2>
            <p className="text-gray-400 max-w-md mx-auto mb-10">
              Book your Restoration Massage and give your body the attention it deserves.
            </p>
            <Link to="/booking?category=mens">
              <Button
                className="rounded-none bg-[#C9A96E] hover:bg-[#b8944f] text-white px-12 py-7 text-xs tracking-[0.2em] uppercase"
                data-testid="mens-cta-book"
              >
                Book Your Treatment
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
